import { useState } from "react";
import { api, API, formatError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Upload, FileText, Check, AlertTriangle, X } from "lucide-react";
import { toast } from "sonner";

const TEMPLATES = {
  leads: "client_name,mobile,email,city,configuration,preferred_location,source,sub_source,campaign,notes\nRahul Sharma,9812345678,rahul@example.com,Mumbai,3 BHK,Bandra,meta,,META-2026-Q1,Interested in 3BHK\n",
  cps: "company_name,contact_person,mobile,email,city,category,tier\nSkyline Realty,Rajesh Khanna,9820011122,rk@skyline.com,Mumbai,gold,A\n",
};

export default function Import() {
  const [module, setModule] = useState("leads");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [skipDupes, setSkipDupes] = useState(true);

  const onPreview = async () => {
    if (!file) return toast.error("Choose a CSV file");
    setBusy(true); setPreview(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await fetch(`${API}/import/preview/${module}`, { method: "POST", body: fd, credentials: "include" });
      const data = await res.json();
      if (!res.ok) throw new Error(formatError(data.detail) || "Preview failed");
      setPreview(data);
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const onCommit = async () => {
    if (!preview) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/import/commit/${module}`, { rows: preview.preview, skip_duplicates: skipDupes });
      toast.success(`Imported: ${data.created} · Skipped: ${data.skipped} · Errors: ${data.errored}`);
      setPreview(null); setFile(null);
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATES[module]], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${module}_template.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const iconFor = (s) => s === "new" ? <Check size={12} className="text-emerald-600"/> : s === "duplicate" ? <AlertTriangle size={12} className="text-amber-600"/> : <X size={12} className="text-rose-600"/>;

  return (
    <div className="space-y-6 animate-in">
      <div>
        <div className="text-[11px] uppercase tracking-[0.3em] text-muted-foreground">Data</div>
        <h1 className="font-serif-display text-3xl">Bulk Import</h1>
        <p className="text-sm text-muted-foreground mt-1">Upload a CSV, review the duplicate preview, then commit. Nothing is written until you confirm.</p>
      </div>

      <Tabs value={module} onValueChange={(v) => { setModule(v); setPreview(null); setFile(null); }}>
        <TabsList>
          <TabsTrigger value="leads" data-testid="import-tab-leads">Leads</TabsTrigger>
          <TabsTrigger value="cps" data-testid="import-tab-cps">Channel Partners</TabsTrigger>
        </TabsList>
        <TabsContent value={module}>
          <Card className="p-6 space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <label className="inline-flex items-center gap-2 border border-dashed border-border rounded-md px-4 py-3 cursor-pointer hover:bg-accent/5" data-testid="import-file-label">
                <Upload size={14}/>
                <span className="text-sm">{file ? file.name : "Choose CSV file…"}</span>
                <input type="file" accept=".csv" className="hidden" onChange={e => setFile(e.target.files?.[0])} data-testid="import-file-input"/>
              </label>
              <Button onClick={onPreview} disabled={busy || !file} data-testid="import-preview-btn"><FileText size={14} className="mr-1"/> Preview</Button>
              <Button variant="ghost" onClick={downloadTemplate} data-testid="import-template-btn">Download template</Button>
            </div>
          </Card>

          {preview && (
            <Card className="mt-4">
              <div className="p-4 flex flex-wrap items-center justify-between gap-3 border-b border-border">
                <div className="flex gap-4 text-sm">
                  <div><span className="text-muted-foreground">Total</span> <span className="metric-value">{preview.total_rows}</span></div>
                  <div><span className="text-emerald-700">New</span> <span className="metric-value">{preview.counts.new}</span></div>
                  <div><span className="text-amber-700">Duplicate</span> <span className="metric-value">{preview.counts.duplicate}</span></div>
                  <div><span className="text-rose-700">Error</span> <span className="metric-value">{preview.counts.error}</span></div>
                </div>
                <div className="flex items-center gap-3">
                  <label className="text-xs flex items-center gap-2"><input type="checkbox" checked={skipDupes} onChange={e => setSkipDupes(e.target.checked)} data-testid="import-skip-dupes"/> Skip duplicates</label>
                  <Button onClick={onCommit} disabled={busy} data-testid="import-commit-btn">Commit Import</Button>
                </div>
              </div>
              <div className="overflow-auto max-h-[55vh]">
                <table className="w-full text-xs">
                  <thead className="bg-secondary/50 sticky top-0 text-[10px] uppercase tracking-wider text-muted-foreground">
                    <tr>
                      <th className="text-left px-3 py-2">#</th>
                      <th className="text-left px-3 py-2">Status</th>
                      {Object.keys(preview.preview[0]?.data || {}).slice(0, 8).map(k => <th key={k} className="text-left px-3 py-2 whitespace-nowrap">{k}</th>)}
                      <th className="text-left px-3 py-2">Match / Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.preview.map((row) => (
                      <tr key={row.row} className="border-t border-border">
                        <td className="px-3 py-2 font-mono">{row.row}</td>
                        <td className="px-3 py-2"><span className="inline-flex items-center gap-1">{iconFor(row.status)}{row.status}</span></td>
                        {Object.keys(preview.preview[0]?.data || {}).slice(0, 8).map(k => <td key={k} className="px-3 py-2 whitespace-nowrap max-w-[180px] truncate">{row.data[k]}</td>)}
                        <td className="px-3 py-2 text-xs">
                          {row.matches?.[0] && <span className="text-amber-700">→ {row.matches[0].client_code || row.matches[0].cp_code} {row.matches[0].name || row.matches[0].company}</span>}
                          {row.error && <span className="text-rose-700">{row.error}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
